/**
 * Analyze hours: 06:00 to 14:00 America/New_York, every day. The database decides
 * (score_analyze_window in 20270239000100). This module only mirrors it so the
 * button can grey out and count down. It never grants anything.
 */
const ZONE = 'America/New_York';
const OPEN_HOUR = 6;
const CLOSE_HOUR = 14;

export interface AnalyzeWindow {
  open: boolean;
  /** The New York calendar date, the one-report-a-day key. */
  nyDate: string;
  /** Set when closed: the next 6 AM ET. */
  opensAt: Date | null;
  /** Set when open: today's 2 PM ET. */
  closesAt: Date | null;
}

interface Wall {
  y: number;
  m: number;
  d: number;
  h: number;
  min: number;
  s: number;
}

function wall(at: Date): Wall {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: ZONE,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(at);
  const n = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? '0');
  return {
    y: n('year'),
    m: n('month'),
    d: n('day'),
    h: n('hour'),
    min: n('minute'),
    s: n('second'),
  };
}

/** The instant when the New York wall clock reads y-m-d hour:00. DST safe. */
function instantAt(y: number, m: number, d: number, hour: number): Date {
  let guess = Date.UTC(y, m - 1, d, hour, 0, 0);
  for (let i = 0; i < 3; i++) {
    const w = wall(new Date(guess));
    const shown = Date.UTC(w.y, w.m - 1, w.d, w.h, w.min, w.s);
    const wanted = Date.UTC(y, m - 1, d, hour, 0, 0);
    if (shown === wanted) break;
    guess += wanted - shown;
  }
  return new Date(guess);
}

const pad = (n: number) => String(n).padStart(2, '0');

export function analyzeWindow(now: Date): AnalyzeWindow {
  const w = wall(now);
  const nyDate = `${String(w.y)}-${pad(w.m)}-${pad(w.d)}`;
  if (w.h >= OPEN_HOUR && w.h < CLOSE_HOUR)
    return { open: true, nyDate, opensAt: null, closesAt: instantAt(w.y, w.m, w.d, CLOSE_HOUR) };
  if (w.h < OPEN_HOUR)
    return { open: false, nyDate, opensAt: instantAt(w.y, w.m, w.d, OPEN_HOUR), closesAt: null };
  return { open: false, nyDate, opensAt: nextDayOpening(w), closesAt: null };
}

function nextDayOpening(w: Wall): Date {
  const next = new Date(Date.UTC(w.y, w.m - 1, w.d + 1));
  return instantAt(next.getUTCFullYear(), next.getUTCMonth() + 1, next.getUTCDate(), OPEN_HOUR);
}

/** When the next report may start once today's is used: 6 AM ET tomorrow. */
export function opensAfterToday(now: Date): Date {
  return nextDayOpening(wall(now));
}

/** "6 PM", "2 AM": an instant on the Manila clock, whole hours (Manila has no daylight saving). */
function manilaHour(at: Date): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Manila',
    hour: 'numeric',
    hourCycle: 'h12',
  }).formatToParts(at);
  const hour = parts.find((p) => p.type === 'hour')?.value ?? '';
  const period = (parts.find((p) => p.type === 'dayPeriod')?.value ?? '').toUpperCase();
  return `${hour} ${period}`;
}

/**
 * The window that is open now, or the next one to open, on the Manila clock. Worked
 * out from 06:00 and 14:00 New York on that New York date: 6 PM and 2 AM under
 * EDT, 7 PM and 3 AM under EST.
 */
export function manilaWindow(now: Date): { opens: string; closes: string } {
  let w = wall(now);
  if (w.h >= CLOSE_HOUR) {
    const next = new Date(Date.UTC(w.y, w.m - 1, w.d + 1));
    w = { ...w, y: next.getUTCFullYear(), m: next.getUTCMonth() + 1, d: next.getUTCDate() };
  }
  return {
    opens: manilaHour(instantAt(w.y, w.m, w.d, OPEN_HOUR)),
    closes: manilaHour(instantAt(w.y, w.m, w.d, CLOSE_HOUR)),
  };
}

/** "6 AM to 2 PM ET (6 PM to 2 AM Manila)", with the Manila side correct for the date. */
export function hoursLabel(now: Date): string {
  const m = manilaWindow(now);
  return `6 AM to 2 PM ET (${m.opens} to ${m.closes} Manila)`;
}

/** "3 h 10 m", "59 m". A partial minute counts as a whole one. Never below zero. */
export function countdownLabel(ms: number): string {
  const minutes = Math.max(0, Math.ceil(ms / 60_000));
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${String(h)} h ${String(m)} m` : `${String(m)} m`;
}
